import { createSSRApp, createApp, h } from 'vue';
import isSSR from '@/utils/isSSR';
import App from '@/App.vue';
import router from '@/app/router';
import createStore from '@/app/store';
import { createPinia } from 'pinia';
import eventBus from '@/app/utils/eventBus';
import { eventBusProvideKey } from '@/app/composables/useEventBus';
import { VueHeadMixin, createHead } from '@unhead/vue';
import * as filters from '@/filters';
import { QueryCache, QueryClient, VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';
import { Settings } from 'luxon';
import logger from '@/app/logger';
import { CustomError } from '@/utils/errors';
import PrimeVue from 'primevue/config';
import locale from 'primelocale/fi.json';
import { AxiosError } from 'axios';

export default function () {
  Settings.defaultZone = 'Europe/Helsinki';
  const store = createStore(); // Creating new store on each call to avoid cross-request state pollution
  const pinia = createPinia();
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: isSSR ? Infinity : 15 * 60 * 1000,
        gcTime: isSSR ? Infinity : 60 * 60 * 1000,
        retry(failureCount, error) {
          let retryableError = true;
          if (error instanceof CustomError) {
            retryableError = error.code !== 404;
          }
          if (error instanceof AxiosError) {
            retryableError = error.response?.status !== 404 && error.response?.status !== 403;
          }

          const retry = retryableError && failureCount < 3 && !isSSR;
          if (retry) {
            logger.info(`Retrying ${failureCount}. retryableError: ${retryableError}, isSSR: ${isSSR}`);
          } else {
            logger.info(
              `Not retrying. retryableError: ${retryableError}, failureCount: ${failureCount} and isSSR: ${isSSR}`
            );
          }
          return retry;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (err) => {
        if (err instanceof Error) {
          if (err instanceof CustomError) {
            if (err.code === 404) {
              logger.info('QueryClient error:', err);
            } else {
              logger.error('QueryClient resolved to an error.', err);
            }
          } else if (err instanceof AxiosError) {
            if (err.response?.status === 404) {
              logger.info('Axios error:', err);
            } else {
              logger.error('Axios error.', err);
            }
          } else {
            logger.error('QueryClient resolved to an error', err);
          }
        }
      },
    }),
  });
  const queryOptions: VueQueryPluginOptions = { queryClient };

  const rootComponent = {
    render: () => h(App),
    components: { App },
  };
  const app = (isSSR ? createSSRApp : createApp)(rootComponent);

  const head = createHead();
  app.mixin(VueHeadMixin);
  app.use(router);
  app.use(store);
  app.use(pinia);
  app.use(head);
  app.use(VueQueryPlugin, queryOptions);
  app.use(PrimeVue, {
    theme: {
      unstyled: true,
    },
    locale: locale.fi,
  });
  app.config.globalProperties.$bus = eventBus;
  app.provide(eventBusProvideKey, eventBus);
  app.config.globalProperties.$filters = filters;
  app.config.globalProperties.$isSSR = isSSR;

  return { app, router, queryClient, head, store };
}
